/**
 * TeamStore — the teams feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY teams module that talks to the API, and it
 * does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<Team>('teams')` + `api.identityProvider`). It never imports
 * ApiClient or calls fetch. React hooks call this store; pages call the hook.
 *
 * Persistence model: each team is one document with id `team_<uuid>`. The roster
 * is short, so members are mirrored (as JSON) onto the DDB pointer row alongside
 * name/description/counts; {@link listSummaries} reconstructs full team summaries
 * (including members) WITHOUT hydrating S3 bodies — which is what lets the
 * people directory build cheaply from the list.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 *
 * NO backend changes are required: this reuses the generic document store.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type { Team, TeamDraft, TeamMember, TeamSummary } from './types';
import { cleanMembers, teamDocId } from './teamModel';

export const TEAMS_COLLECTION = 'teams';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  name: 'name',
  description: 'description',
  members: 'members',
  memberCount: 'memberCount',
  createdBy: 'createdBy',
  createdAt: 'createdAt',
  lastUpdated: 'lastUpdated',
  deleted: 'deleted',
} as const;

/** Safely parses the mirrored members JSON from a pointer row. */
function parseMembers(raw?: string): TeamMember[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((m) => ({
        name: typeof m?.name === 'string' ? m.name : '',
        alias: typeof m?.alias === 'string' ? m.alias : '',
        role: typeof m?.role === 'string' ? m.role : '',
        email: typeof m?.email === 'string' ? m.email : '',
      }))
      .filter((m) => m.alias || m.name);
  } catch {
    return [];
  }
}

export class TeamStore {
  private readonly docs: DocumentStore<Team>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<Team>(TEAMS_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(team: Team): StringMap {
    const members = cleanMembers(team.members);
    return {
      [ROW.name]: team.name ?? '',
      [ROW.description]: team.description ?? '',
      [ROW.members]: JSON.stringify(members),
      [ROW.memberCount]: String(members.length),
      [ROW.createdBy]: team.createdBy ?? '',
      [ROW.createdAt]: team.createdAt ?? '',
      [ROW.lastUpdated]: team.lastUpdated ?? '',
      [ROW.deleted]: team.deleted ? 'true' : 'false',
    };
  }

  /** Reconstructs a full team summary from a pointer row. */
  private toSummary(row: StringMap): TeamSummary {
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      name: row[ROW.name] ?? '',
      description: row[ROW.description] ?? '',
      members: parseMembers(row[ROW.members]),
      createdBy: row[ROW.createdBy] ?? '',
      createdAt: row[ROW.createdAt] ?? '',
      lastUpdated: row[ROW.lastUpdated] ?? '',
    };
  }

  /** Lists non-deleted team summaries (defensively scoped to this collection). */
  async listSummaries(): Promise<TeamSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === TEAMS_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /** Loads a single team document by id. */
  async get(id: string): Promise<Team | undefined> {
    return this.docs.get(id);
  }

  /**
   * Creates a new team. Seeds id/createdBy (from identity)/timestamps, persists
   * it, and returns the saved document.
   */
  async create(draft: TeamDraft): Promise<Team> {
    const identity = await this.api.identityProvider.getIdentity();
    const now = new Date().toISOString();
    const team: Team = {
      id: teamDocId(),
      name: draft.name.trim(),
      description: draft.description ?? '',
      members: cleanMembers(draft.members),
      createdBy: identity.alias,
      createdAt: now,
      lastUpdated: now,
      deleted: false,
    };
    await this.docs.save(team.id, team, this.indexAttributes(team));
    return team;
  }

  /** Applies a draft's fields to an existing team in place (same id/createdBy). */
  async applyDraft(team: Team, draft: TeamDraft): Promise<Team> {
    const updated: Team = {
      ...team,
      name: draft.name.trim(),
      description: draft.description ?? '',
      members: cleanMembers(draft.members),
      lastUpdated: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /**
   * Soft-deletes a team: fetches it, flags it deleted, and updates both the S3
   * body and the pointer row so list() filters it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: Team = {
      ...existing,
      deleted: true,
      lastUpdated: new Date().toISOString(),
    };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }
}
